import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  Form,
  Icon,
  Message,
  Modal,
  Pagination,
  Table,
} from 'semantic-ui-react';
import { API, isRoot } from '../../helpers';
import {
  EXPORT_API_V1014,
  activeExportV1014,
  durationV1014,
  exportTextV1014,
  presetV1014,
  validRangeV1014,
} from './exportV1014';
import './exportV1014.css';

export default function ConsumptionExportV1014() {
  const { i18n } = useTranslation();
  const text = useCallback(
    (key) => exportTextV1014(i18n.language, key),
    [i18n.language]
  );
  const [params] = useSearchParams();
  const scope = params.get('scope') === 'special' ? 'special' : 'ordinary';
  const back =
    scope === 'ordinary'
      ? '/log'
      : isRoot()
      ? '/organization-log'
      : '/vertex-log/customer';
  const [jobs, setJobs] = useState([]);
  const [active, setActive] = useState(0);
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('tasks');
  const [status, setStatus] = useState('');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(() => presetV1014(7));
  const [preset, setPreset] = useState(7);
  const [mode, setMode] = useState('key');
  const [tokens, setTokens] = useState([]);
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState('');
  const [tokenLoading, setTokenLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState('');
  const [clock, setClock] = useState(Date.now());
  const offset = useRef(0);
  const request = useRef(0);
  const dialogOpener = useRef(null);
  useEffect(() => {
    const id = open
      ? 'export-create-v1014'
      : deleting
      ? 'export-delete-v1014'
      : null;
    if (!id) return undefined;
    const focusable = () =>
      Array.from(
        document
          .getElementById(id)
          ?.querySelectorAll(
            'button, input, select, textarea, a[href], [tabindex]'
          ) || []
      ).filter(
        (element) =>
          !element.disabled &&
          element.tabIndex >= 0 &&
          element.getClientRects().length
      );
    const timer = setTimeout(() => focusable()[0]?.focus(), 0);
    const trap = (event) => {
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const index = items.indexOf(document.activeElement);
      if (
        index < 0 ||
        (event.shiftKey && index === 0) ||
        (!event.shiftKey && index === items.length - 1)
      ) {
        event.preventDefault();
        items[event.shiftKey ? items.length - 1 : 0].focus();
      }
    };
    document.addEventListener('keydown', trap);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', trap);
      const opener = dialogOpener.current;
      setTimeout(
        () =>
          (opener?.isConnected && !opener.disabled
            ? opener
            : document.getElementById('export-refresh-v1014')
          )?.focus(),
        0
      );
    };
  }, [open, deleting]);
  const load = useCallback(async () => {
    const generation = ++request.current;
    try {
      const response = await API.get(EXPORT_API_V1014, { params: { scope } });
      if (generation !== request.current) return;
      const data = response?.data;
      if (!data?.success)
        throw new Error(data?.message || 'export_unavailable');
      setJobs(data.data || []);
      setActive(data.active_count || 0);
      setAvailable(data.available === true);
      offset.current = data.server_time * 1000 - Date.now();
      setClock(Date.now());
      setError('');
    } catch (e) {
      if (generation === request.current) setError(e.message);
    } finally {
      if (generation === request.current) setLoading(false);
    }
  }, [scope]);
  useEffect(() => {
    setLoading(true);
    setJobs([]);
    setPage(1);
    setOpen(false);
    load();
    return () => {
      request.current++;
    };
  }, [load]);
  useEffect(() => {
    const tick = setInterval(() => setClock(Date.now()), 1000);
    const poll = setInterval(() => {
      if (!document.hidden && jobs.some(activeExportV1014)) load();
    }, 6000);
    const focus = () => {
      if (!document.hidden) load();
    };
    document.addEventListener('visibilitychange', focus);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
      document.removeEventListener('visibilitychange', focus);
    };
  }, [jobs, load]);
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const timer = setTimeout(async () => {
      setTokenLoading(true);
      try {
        const response = await API.get(`${EXPORT_API_V1014}/tokens`, {
          params: { scope, search: query },
        });
        if (!alive) return;
        if (!response?.data?.success) throw new Error('export_unavailable');
        setTokens((previous) => {
          const kept = previous.filter((token) => selected.includes(token.id));
          const map = new Map(
            [...kept, ...response.data.data].map((token) => [token.id, token])
          );
          return [...map.values()];
        });
      } catch (e) {
        if (alive) setFormError(e.message);
      } finally {
        if (alive) setTokenLoading(false);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [open, query, scope, selected]);

  const now = Math.floor((clock + offset.current) / 1000);
  const normalized = jobs.map((job) =>
    job.status === 'completed' && job.expires_at <= now
      ? { ...job, status: 'expired' }
      : job
  );
  const visible = normalized.filter(
    (job) =>
      (!filter || job.status === filter) &&
      (tab !== 'history' || !activeExportV1014(job))
  );
  const pages = Math.max(1, Math.ceil(visible.length / 10));
  const activePage = Math.min(page, pages);
  const rows = visible.slice((activePage - 1) * 10, activePage * 10);
  const date = (value) =>
    value
      ? new Date(value * 1000).toLocaleString(
          i18n.language?.startsWith('en') ? 'en-GB' : 'zh-CN',
          { hour12: false }
        )
      : '-';
  const fail = (e) =>
    text(e?.response?.data?.message || e?.message || 'export_unavailable');
  const create = async () => {
    if (!validRangeV1014(range)) {
      setFormError('invalidRange');
      return;
    }
    if (active >= 2) {
      setFormError('export_user_limit');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const response = await API.post(EXPORT_API_V1014, {
        scope,
        mode,
        token_ids: selected,
        start_timestamp: Math.floor(new Date(range.start).getTime() / 1000),
        end_timestamp: Math.floor(new Date(range.end).getTime() / 1000),
        timezone_offset: -new Date().getTimezoneOffset(),
      });
      if (!response?.data?.success)
        throw new Error(response?.data?.message || 'export_unavailable');
      setOpen(false);
      setTab('tasks');
      setFilter('');
      setStatus('');
      setPage(1);
      await load();
    } catch (e) {
      setFormError(e?.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };
  const download = async (job) => {
    setBusy(job.id);
    setError('');
    try {
      const response = await API.get(`${EXPORT_API_V1014}/${job.id}/download`, {
        responseType: 'blob',
      });
      if (!response?.data || response.data.type?.includes('json'))
        throw new Error('export_not_ready');
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `consumption-${job.id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };
  const remove = async () => {
    setBusy(deleting.id);
    try {
      const response = await API.delete(`${EXPORT_API_V1014}/${deleting.id}`);
      if (!response?.data?.success)
        throw new Error(response?.data?.message || 'export_unavailable');
      setDeleting(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <main className='dashboard-container management-container-v4 consumption-export-v1014'>
      <nav className='export-breadcrumb-v1014' aria-label={text('logs')}>
        <Link to={back}>{text(scope === 'special' ? 'special' : 'logs')}</Link>
        <Icon name='angle right' />
        <span>{text('title')}</span>
      </nav>
      <header className='export-heading-v1014'>
        <h2>{text('title')}</h2>
        <Button
          id='export-refresh-v1014'
          icon='refresh'
          basic
          title={text('refresh')}
          aria-label={text('refresh')}
          loading={loading}
          onClick={load}
        />
      </header>
      <div
        className='export-tabs-v1014'
        role='tablist'
        aria-label={text('title')}
      >
        {['tasks', 'history'].map((name) => (
          <button
            key={name}
            type='button'
            role='tab'
            id={`export-tab-${name}`}
            aria-controls='export-panel-v1014'
            aria-selected={tab === name}
            tabIndex={tab === name ? 0 : -1}
            className={tab === name ? 'active' : ''}
            onKeyDown={(event) => {
              if (
                !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)
              )
                return;
              event.preventDefault();
              const next =
                event.key === 'Home'
                  ? 'tasks'
                  : event.key === 'End'
                  ? 'history'
                  : name === 'tasks'
                  ? 'history'
                  : 'tasks';
              setTab(next);
              setPage(1);
              document.getElementById(`export-tab-${next}`)?.focus();
            }}
            onClick={() => {
              setTab(name);
              setPage(1);
            }}
          >
            {text(name)}
          </button>
        ))}
      </div>
      <section
        id='export-panel-v1014'
        role='tabpanel'
        aria-labelledby={`export-tab-${tab}`}
      >
        {error && <Message negative content={fail(new Error(error))} />}
        {!loading && !error && !available && (
          <Message warning content={text('export_cpu_quota_unavailable')} />
        )}
        <div className='export-toolbar-v1014'>
          <div className='export-filters-v1014'>
            <Dropdown
              selection
              aria-label={text('status')}
              placeholder={text('all')}
              value={status}
              options={[
                '',
                'queued',
                'running',
                'completed',
                'failed',
                'expired',
              ].map((value) => ({
                key: value || 'all',
                value,
                text: text(value || 'all'),
              }))}
              onChange={(_, data) => setStatus(data.value)}
            />
            <Button
              basic
              onClick={() => {
                setStatus('');
                setFilter('');
                setPage(1);
              }}
            >
              {text('reset')}
            </Button>
            <Button
              onClick={() => {
                setFilter(status);
                setPage(1);
              }}
            >
              {text('search')}
            </Button>
          </div>
          <Button
            primary
            icon
            labelPosition='left'
            disabled={loading || !available || active >= 2}
            title={active >= 2 ? text('export_user_limit') : undefined}
            onClick={(event) => {
              dialogOpener.current = event.currentTarget;
              setRange(presetV1014(7));
              setPreset(7);
              setSelected([]);
              setQuery('');
              setMode('key');
              setFormError('');
              setOpen(true);
            }}
          >
            <Icon name='plus' />
            {text('create')}
          </Button>
        </div>
        {active >= 2 && <Message info content={text('export_user_limit')} />}
        <div
          className='export-table-scroll-v1014'
          role='region'
          aria-label={text(tab)}
          tabIndex={0}
        >
          <Table unstackable basic='very' className='export-table-v1014'>
            <Table.Header>
              <Table.Row>
                {[
                  'id',
                  'type',
                  'object',
                  'range',
                  'status',
                  'rows',
                  'expiry',
                  'created',
                  ...(tab === 'history' ? ['downloads'] : []),
                  'actions',
                ].map((name) => (
                  <Table.HeaderCell key={name}>{text(name)}</Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((job) => (
                <Table.Row key={job.id}>
                  <Table.Cell title={job.id}>{job.id.slice(0, 8)}</Table.Cell>
                  <Table.Cell>
                    {text('usage')}
                    <small>
                      {job.mode === 'daily'
                        ? text('daily').split('，')[0]
                        : 'Key'}
                    </small>
                  </Table.Cell>
                  <Table.Cell>
                    {job.token_ids?.length
                      ? `${job.token_ids.length} Key`
                      : text('visible')}
                  </Table.Cell>
                  <Table.Cell>
                    <details className='export-mobile-identity-v1014'>
                      <summary>
                        {job.id.slice(0, 8)} ·{' '}
                        {job.mode === 'daily' ? text('dailyShort') : 'Key'}
                      </summary>
                      <div>
                        {text('id')}: {job.id}
                      </div>
                      <div>
                        {text('object')}:{' '}
                        {job.token_ids?.length
                          ? `${job.token_ids.length} Key`
                          : text('visible')}
                      </div>
                      <div>
                        {text('created')}: {date(job.created_at)}
                      </div>
                    </details>
                    <time>{date(job.start_timestamp)}</time>
                    <small>{date(job.end_timestamp)}</small>
                  </Table.Cell>
                  <Table.Cell>
                    <span className={`export-state-v1014 ${job.status}`}>
                      <Icon
                        name={
                          {
                            queued: 'clock outline',
                            running: 'circle notched',
                            completed: 'check circle',
                            failed: 'warning circle',
                            expired: 'clock outline',
                          }[job.status]
                        }
                        loading={job.status === 'running'}
                      />
                      {text(job.status)}
                    </span>
                    {activeExportV1014(job) && (
                      <small>
                        {text('eta')} {durationV1014(job.eta_seconds)}
                      </small>
                    )}
                    {job.error && (
                      <small className='export-error-v1014'>
                        {text(job.error)}
                      </small>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {Number(job.rows || 0).toLocaleString()}
                    <small>
                      {job.bytes
                        ? `${(job.bytes / 1024).toFixed(1)} KB`
                        : job.total > 0
                        ? `/ ${Number(job.total).toLocaleString()}`
                        : '-'}
                    </small>
                  </Table.Cell>
                  <Table.Cell>
                    {job.status === 'completed' ? (
                      <>
                        <span>{durationV1014(job.expires_at - now)}</span>
                        <progress
                          max={2700}
                          value={Math.max(0, job.expires_at - now)}
                          aria-label={text('expiry')}
                        />
                      </>
                    ) : (
                      '-'
                    )}
                  </Table.Cell>
                  <Table.Cell>{date(job.created_at)}</Table.Cell>
                  {tab === 'history' && (
                    <Table.Cell>
                      {job.downloads || 0}
                      <small title={text('lastDownload')}>
                        {date(job.last_downloaded_at)}
                      </small>
                    </Table.Cell>
                  )}
                  <Table.Cell>
                    <div className='export-actions-v1014'>
                      <Button
                        icon='download'
                        basic
                        size='mini'
                        aria-label={text('download')}
                        title={text('download')}
                        disabled={job.status !== 'completed' || busy === job.id}
                        loading={busy === job.id && !deleting}
                        onClick={() => download(job)}
                      />
                      <Button
                        icon='trash alternate outline'
                        basic
                        size='mini'
                        aria-label={text('remove')}
                        title={text('remove')}
                        disabled={!!busy}
                        onClick={(event) => {
                          dialogOpener.current = event.currentTarget;
                          setDeleting(job);
                        }}
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
              {!rows.length && (
                <Table.Row>
                  <Table.Cell colSpan={tab === 'history' ? 11 : 10}>
                    <div className='export-empty-v1014'>
                      <Icon
                        name={
                          loading ? 'circle notched' : 'file alternate outline'
                        }
                        loading={loading}
                        size='big'
                      />
                      <span>{loading ? text('queued') : text('empty')}</span>
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
        {pages > 1 && (
          <Pagination
            activePage={activePage}
            totalPages={pages}
            onPageChange={(_, data) => setPage(data.activePage)}
          />
        )}
      </section>
      <Modal
        id='export-create-v1014'
        role='dialog'
        aria-modal='true'
        aria-labelledby='export-create-title-v1014'
        open={open}
        size='small'
        className='export-modal-v1014'
        closeOnEscape={!saving}
        closeOnDimmerClick={!saving}
        onClose={() => {
          if (!saving) setOpen(false);
        }}
      >
        <Modal.Header id='export-create-title-v1014'>
          <span>{text('modal')}</span>
          <Button
            type='button'
            basic
            icon='close'
            disabled={saving}
            aria-label={text('close')}
            title={text('close')}
            onClick={() => setOpen(false)}
          />
        </Modal.Header>
        <Modal.Content>
          <Form onSubmit={create}>
            <Form.Field>
              <label>{text('quick')}</label>
              <div
                className='export-presets-v1014'
                role='group'
                aria-label={text('quick')}
              >
                {[1, 3, 7, 30].map((days) => (
                  <button
                    key={days}
                    type='button'
                    aria-pressed={preset === days}
                    onClick={() => {
                      setPreset(days);
                      setRange(presetV1014(days));
                    }}
                  >
                    {days}
                    {days === 1 && i18n.language?.startsWith('en')
                      ? ' day'
                      : text('days')}
                  </button>
                ))}
              </div>
            </Form.Field>
            <Form.Group widths='equal'>
              <Form.Input
                id='export-start-v1014'
                required
                type='datetime-local'
                label={text('start')}
                value={range.start}
                onChange={(_, data) => {
                  setPreset(null);
                  setRange({ ...range, start: data.value });
                }}
              />
              <Form.Input
                id='export-end-v1014'
                required
                type='datetime-local'
                label={text('end')}
                value={range.end}
                onChange={(_, data) => {
                  setPreset(null);
                  setRange({ ...range, end: data.value });
                }}
              />
            </Form.Group>
            <Form.Field>
              <label id='export-token-label-v1014'>{text('tokens')}</label>
              <Dropdown
                fluid
                multiple
                selection
                search
                clearable
                loading={tokenLoading}
                placeholder={text('tokenPlaceholder')}
                aria-label={text('tokens')}
                aria-labelledby='export-token-label-v1014'
                value={selected}
                options={tokens.map((token) => ({
                  key: token.id,
                  value: token.id,
                  text: `${token.name || 'Key'} (#${token.id})`,
                }))}
                onSearchChange={(_, data) => setQuery(data.searchQuery)}
                onChange={(_, data) => setSelected(data.value)}
              />
            </Form.Field>
            <Form.Group
              grouped
              role='radiogroup'
              aria-labelledby='export-format-label-v1014'
            >
              <label id='export-format-label-v1014'>{text('format')}</label>
              <Form.Radio
                id='export-mode-key-v1014'
                label={text('key')}
                name='export-format'
                checked={mode === 'key'}
                onChange={() => setMode('key')}
              />
              <Form.Radio
                id='export-mode-daily-v1014'
                label={text('daily')}
                name='export-format'
                checked={mode === 'daily'}
                onChange={() => setMode('daily')}
              />
            </Form.Group>
            {formError && (
              <Message visible negative content={text(formError)} />
            )}
            <div className='export-modal-actions-v1014'>
              <Button
                type='button'
                basic
                disabled={saving}
                onClick={() => setOpen(false)}
              >
                {text('cancel')}
              </Button>
              <Button
                type='submit'
                primary
                loading={saving}
                disabled={
                  saving || !validRangeV1014(range) || selected.length > 50
                }
              >
                {text('submit')}
              </Button>
            </div>
          </Form>
        </Modal.Content>
      </Modal>
      <Modal
        id='export-delete-v1014'
        role='dialog'
        aria-modal='true'
        aria-labelledby='export-delete-title-v1014'
        open={!!deleting}
        size='mini'
        closeOnEscape={!busy}
        closeOnDimmerClick={!busy}
        onClose={() => {
          if (!busy) setDeleting(null);
        }}
      >
        <Modal.Header id='export-delete-title-v1014'>
          {text('confirm')}
        </Modal.Header>
        <Modal.Actions>
          <Button basic disabled={!!busy} onClick={() => setDeleting(null)}>
            {text('cancel')}
          </Button>
          <Button negative loading={!!busy} onClick={remove}>
            {text('remove')}
          </Button>
        </Modal.Actions>
      </Modal>
    </main>
  );
}
